import { useCallback, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fl, trName } from "@/lib/finI18n";
import { coaName } from "@/lib/masterI18n";
import { tkuOf } from "@/lib/kop";
import { ComboBox } from "@/components/ComboBox";
import { MasterImportModal } from "@/components/MasterImportModal";
import { TaxIdImport } from "@/components/txn/TaxIdImport";
import { TaxIdCheck } from "@/components/txn/TaxIdReminder";
import { useTableTools, TableToolbar, SelectAllBox, SelectBox, deleteEach } from "@/components/TableTools";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, AlertTriangle, CheckCheck } from "lucide-react";

const KINDS = ["vendor", "customer", "employee", "other"];

export function LedgerTab({ clientId, coa }) {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [coaF, setCoaF] = useState("");
  const [edit, setEdit] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [recheckOnly, setRecheckOnly] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    const p = new URLSearchParams({ client_id: clientId, limit: "2000" });
    if (coaF) p.set("coa_id", coaF);
    const { data } = await api.get(`/ledgers?${p.toString()}`);
    setItems(data);
  }, [clientId, coaF]);
  useEffect(() => { load(); }, [load]);
  const coaLbl = (l) => (l.coa_code ? `${l.coa_code} · ${(lang === "en" && l.coa_name_en) || (lang === "zh" && l.coa_name_zh) || l.coa_name}` : "");
  const recheck = items.filter((l) => l.tax_id_check);
  useEffect(() => { if (recheckOnly && !recheck.length) setRecheckOnly(false); }, [recheckOnly, recheck.length]);
  const tools = useTableTools(recheckOnly ? recheck : items, { date: (r) => r.created_at, text: (l) => `${l.name} ${l.name_en || ""} ${l.name_zh || ""} ${coaLbl(l)} ${l.npwp || ""} ${l.nik || ""} ${l.id_tku || ""} ${l.notes || ""} ${fl(`k_${l.kind || "vendor"}`, lang)}` });
  const cols = [[fl("ledgerName", lang), (l) => trName(l, lang)], [fl("kind", lang), (l) => fl(`k_${l.kind || "vendor"}`, lang)], [fl("underCoa", lang), coaLbl],
    ["NPWP", "npwp"], ["NIK", "nik"], ["ID TKU", "id_tku"], [t("common.description"), "notes"]];

  const del = async (l) => {
    if (!window.confirm(`Hapus ledger ${l.name}?`)) return;
    try { await api.delete(`/ledgers/${l.id}`); toast.success(t("common.saved")); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const bulkDel = async (ids) => { const r = await deleteEach(ids, (id) => api.delete(`/ledgers/${id}`)); load(); return r; };
  const confirmCheck = async (l) => {
    try { await api.post("/tax-ids/confirm", { client_id: clientId, ledger_id: l.id }); toast.success(fl("tiConfirmed", lang)); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const coaOpts = coa.map((c) => ({ value: c.id, label: `${c.code} · ${coaName(c, lang)}` }));

  return (
    <Card className="p-4" data-testid="ledger-tab">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-head font-semibold">{fl("ledgerTitle", lang)} <span className="text-muted-foreground font-normal text-sm">({tools.view.length})</span></h2>
        <TableToolbar tools={tools} id="ledger" cols={cols} filename="ledger" onDelete={bulkDel}>
          <div className="w-56"><ComboBox testid="ledger-coa-filter" value={coaF} onChange={setCoaF} placeholder={fl("allCoa", lang)} searchPlaceholder={fl("searchCoa", lang)} options={[{ value: "", label: fl("allCoa", lang) }, ...coaOpts]} /></div>
          <Button variant="outline" size="sm" className="h-9" onClick={() => setImportOpen(true)} data-testid="ledger-import"><Upload className="h-4 w-4 mr-1" />Import</Button>
          <TaxIdImport clientId={clientId} lang={lang} onDone={load} />
          {(recheck.length > 0 || recheckOnly) && (
            <Button variant={recheckOnly ? "default" : "outline"} size="sm" className="h-9 border-amber-400" onClick={() => setRecheckOnly(!recheckOnly)} data-testid="ledger-recheck-filter">
              <AlertTriangle className="h-4 w-4 mr-1 text-amber-500" />{`${fl("tiRecheckFilter", lang)} (${recheck.length})`}
            </Button>
          )}
          <Button size="sm" className="h-9" onClick={() => setEdit({})} data-testid="ledger-add-btn"><Plus className="h-4 w-4 mr-1" />{t("common.add")}</Button>
        </TableToolbar>
      </div>
      <div className="overflow-auto rounded-md border border-border">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-8"><SelectAllBox tools={tools} id="ledger" /></TableHead>
            <TableHead>{fl("ledgerName", lang)}</TableHead><TableHead>{fl("kind", lang)}</TableHead>
            <TableHead>{fl("underCoa", lang)}</TableHead><TableHead>{fl("npwpNik", lang)}</TableHead><TableHead>{fl("idTku", lang)}</TableHead><TableHead /><TableHead className="text-right">{t("common.actions")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {tools.view.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">{t("common.noData")}</TableCell></TableRow>}
            {tools.view.map((l) => (
              <TableRow key={l.id} data-testid={`ledger-row-${l.id}`}>
                <TableCell><SelectBox tools={tools} row={l} id="ledger" /></TableCell>
                <TableCell className="text-sm font-medium">{trName(l, lang)}{(trName(l, lang) !== l.name || l.bank_id) && <span className="block text-[11px] text-muted-foreground">{l.bank_id ? l.notes : l.name}</span>}</TableCell>
                <TableCell className="text-xs">{fl(`k_${l.kind || "vendor"}`, lang)}</TableCell>
                <TableCell className="text-xs">{l.coa_code ? coaLbl(l) : <span className="text-muted-foreground">{fl("noCoa", lang)}</span>}</TableCell>
                <TableCell className="text-xs num" data-testid={`ledger-taxid-${l.id}`}>{l.npwp || l.nik ? <>{l.npwp && <span className="block">NPWP {l.npwp}</span>}{l.nik && <span className="block">NIK {l.nik}</span>}</> : "—"}
                  {l.tax_id_issues?.length > 0 && (
                    <span className="mt-0.5 flex items-center gap-1 text-[10px] text-amber-700 dark:text-amber-300" data-testid={`ledger-taxid-warn-${l.id}`}
                      title={l.tax_id_issues.map((c) => fl(`tiIss_${c}`, lang)).join("\n")}>
                      <AlertTriangle className="h-3 w-3" />{fl(`tiIss_${l.tax_id_issues[0]}`, lang)}
                    </span>
                  )}
                  {l.tax_id_check && (
                    <span className="mt-1 inline-flex items-center gap-1">
                      <span className="rounded bg-amber-100 dark:bg-amber-900/40 px-1.5 text-[10px] font-medium text-amber-800 dark:text-amber-300" data-testid={`ledger-recheck-${l.id}`}
                        title={`${fl("tiRecheckTip", lang)} ${[l.tax_id_check.old_npwp && `NPWP ${l.tax_id_check.old_npwp}`, l.tax_id_check.old_nik && `NIK ${l.tax_id_check.old_nik}`].filter(Boolean).join(" · ")}`}>
                        {fl("tiRecheck", lang)}: {fl("tiOld", lang)} {l.tax_id_check.old_npwp || l.tax_id_check.old_nik}
                      </span>
                      <Button variant="ghost" size="icon" className="h-6 w-6" title={fl("tiConfirm", lang)} onClick={() => confirmCheck(l)} data-testid={`ledger-recheck-ok-${l.id}`}><CheckCheck className="h-3.5 w-3.5 text-emerald-600" /></Button>
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-xs num" data-testid={`ledger-tku-${l.id}`}>{l.id_tku || "—"}</TableCell>
                <TableCell><Badge variant="outline" className="text-[10px]">{l.auto || l.system ? fl("auto", lang) : fl("manual", lang)}</Badge></TableCell>
                <TableCell className="text-right whitespace-nowrap">{!l.bank_id && <>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEdit(l)} data-testid={`ledger-edit-${l.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => del(l)} data-testid={`ledger-del-${l.id}`}><Trash2 className="h-4 w-4" /></Button>
                </>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {edit && <LedgerForm clientId={clientId} item={edit} coaOpts={coaOpts} onClose={() => setEdit(null)} onDone={load} />}
      <MasterImportModal open={importOpen} onOpenChange={setImportOpen} clientId={clientId} type="ledgers" label={fl("ledgerTitle", lang)} onImported={load} />
    </Card>
  );
}

function LedgerForm({ clientId, item, coaOpts, onClose, onDone }) {
  const { t, lang } = useI18n();
  const [f, setF] = useState({ name: item.name || "", coa_id: item.coa_id || "", kind: item.kind || "vendor", npwp: item.npwp || "", nik: item.nik || "", id_tku: item.id_tku && item.id_tku !== tkuOf(item.npwp, item.nik) ? item.id_tku : "", notes: item.notes || "" });
  const autoTku = tkuOf(f.npwp, f.nik);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Nama ledger wajib diisi"); return; }
    try {
      const payload = { client_id: clientId, ...f };
      if (item.id) await api.put(`/ledgers/${item.id}`, payload); else await api.post("/ledgers", payload);
      toast.success(t("common.saved")); onClose(); onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="ledger-form">
        <DialogHeader><DialogTitle>{item.id ? t("common.edit") : t("common.add")} · {fl("ledger", lang)}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>{fl("ledgerName", lang)}</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="ledger-name" /></div>
          <div className="space-y-1.5"><Label>{fl("underCoa", lang)}</Label><ComboBox testid="ledger-coa" value={f.coa_id} onChange={(v) => setF({ ...f, coa_id: v })} placeholder={fl("noCoa", lang)} searchPlaceholder={fl("searchCoa", lang)} options={coaOpts} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>{fl("kind", lang)}</Label>
              <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
                <SelectTrigger data-testid="ledger-kind"><SelectValue /></SelectTrigger>
                <SelectContent>{KINDS.map((k) => <SelectItem key={k} value={k}>{fl(`k_${k}`, lang)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>NPWP</Label><Input value={f.npwp} onChange={(e) => setF({ ...f, npwp: e.target.value })} className="num" data-testid="ledger-npwp" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>{fl("nik", lang)}</Label><Input value={f.nik} onChange={(e) => setF({ ...f, nik: e.target.value })} className="num" data-testid="ledger-nik" /></div>
            <div className="space-y-1.5"><Label>{fl("idTku", lang)}</Label>
              <Input value={f.id_tku} onChange={(e) => setF({ ...f, id_tku: e.target.value })} placeholder={autoTku || "ID TKU"} className="num" data-testid="ledger-id-tku" />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground -mt-1" data-testid="ledger-tku-hint">{fl("tkuAuto", lang)}</p>
          <TaxIdCheck npwp={f.npwp} nik={f.nik} lang={lang} testid="ledger-taxid-check" />
          <div className="space-y-1.5"><Label>{t("common.description")}</Label><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="ledger-notes" /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="ledger-save">{t("common.save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
